/** Exact rational arithmetic for prices, FX, percentages and allocation weights. */
export class MoneyFraction {
  readonly n: bigint;
  readonly d: bigint;
  constructor(n: bigint, d = BigInt(1)) {
    if (d <= BigInt(0)) throw new Error("Invalid monetary divisor");
    let a = n < BigInt(0) ? -n : n;
    let b = d;
    while (b) { const remainder = a % b; a = b; b = remainder; }
    this.n = n / (a || BigInt(1)); this.d = d / (a || BigInt(1));
  }
  static from(value: number | string): MoneyFraction {
    if ((typeof value !== "number" && typeof value !== "string") || String(value).trim() === "" || !Number.isFinite(Number(value))) throw new Error("Invalid monetary amount");
    const match = /^([+-]?)(\d+)(?:\.(\d*))?(?:e([+-]?\d+))?$/i.exec(String(value));
    if (!match) throw new Error("Invalid monetary amount");
    const exponent = Number(match[4] || 0) - (match[3] || "").length;
    if (Math.abs(exponent) > 100) throw new Error("Monetary amount is too large or precise");
    const digits = BigInt(`${match[1]}${match[2]}${match[3] || ""}`);
    return exponent >= 0 ? new MoneyFraction(digits * BigInt(10) ** BigInt(exponent)) : new MoneyFraction(digits, BigInt(10) ** BigInt(-exponent));
  }
  add(value: MoneyFraction): MoneyFraction { return new MoneyFraction(this.n * value.d + value.n * this.d, this.d * value.d); }
  sub(value: MoneyFraction): MoneyFraction { return new MoneyFraction(this.n * value.d - value.n * this.d, this.d * value.d); }
  mul(value: MoneyFraction): MoneyFraction { return new MoneyFraction(this.n * value.n, this.d * value.d); }
  div(value: MoneyFraction): MoneyFraction {
    if (value.n <= BigInt(0)) throw new Error("Invalid monetary divisor");
    return new MoneyFraction(this.n * value.d, this.d * value.n);
  }
  number(): number {
    const result = Number(this.n) / Number(this.d);
    if (!Number.isFinite(result) || Math.abs(result) > 1e12) throw new Error("Monetary calculation exceeds supported range");
    return result;
  }
  /** Half-up currency rounding; ceiling is reserved for the selected final passenger policy. */
  units(precision: number, ceiling = false): bigint {
    if (this.n < BigInt(0)) return -new MoneyFraction(-this.n, this.d).units(precision, ceiling);
    const scaled = this.n * BigInt(10) ** BigInt(precision);
    const quotient = scaled / this.d;
    const remainder = scaled % this.d;
    return quotient + (remainder > BigInt(0) && (ceiling || remainder * BigInt(2) >= this.d) ? BigInt(1) : BigInt(0));
  }
}

/** Returns the ISO currency's supported minor-unit precision. */
export function currencyPrecision(currency: string): number {
  if (!/^[A-Z]{3}$/.test(currency)) throw new Error("Invalid quote currency");
  return new Intl.NumberFormat("en", { style: "currency", currency }).resolvedOptions().maximumFractionDigits ?? 2;
}

/** Allocates minor units by largest remainder, breaking ties by stable source key. */
export function allocateMoney(total: MoneyFraction, weights: ReadonlyArray<{ key: string; weight: MoneyFraction }>, precision: number): number[] {
  const sum = weights.reduce((value, row) => value.add(row.weight), MoneyFraction.from(0));
  if (!sum.n) {
    if (total.n) throw new Error("A nonzero charge has no eligible allocation weights");
    return weights.map(() => 0);
  }
  const target = total.units(precision);
  if (target > BigInt(Number.MAX_SAFE_INTEGER) || target < -BigInt(Number.MAX_SAFE_INTEGER)) throw new Error("Monetary minor units exceed supported range");
  const parts = weights.map((row, index) => {
    const exact = total.mul(row.weight).div(sum).mul(MoneyFraction.from(10 ** precision));
    return { index, key: row.key, units: exact.n / exact.d, remainder: new MoneyFraction(exact.n % exact.d, exact.d) };
  });
  let remaining = target - parts.reduce((value, row) => value + row.units, BigInt(0));
  const ranked = [...parts].sort((a, b) => {
    const difference = b.remainder.n * a.remainder.d - a.remainder.n * b.remainder.d;
    return difference ? difference > BigInt(0) ? 1 : -1 : a.key.localeCompare(b.key);
  });
  for (const row of ranked) { if (remaining <= BigInt(0)) break; row.units++; remaining--; }
  if (remaining) throw new Error("Monetary allocation did not reconcile");
  return parts.map((row) => Number(row.units) / 10 ** precision);
}
